/** Layout helpers for property editors in the task panel. */
import type { PropDef } from "../core/schema";

/** How much room a property takes in the task panel's two-column grid. */
export const propSpan = (def: PropDef): "full" | "half" => {
  const spec = def.spec as { multi?: boolean };
  return def.kind === "text" || def.kind === "geo" || (def.kind === "enum" && spec.multi) ? "full" : "half";
};

/** Mirrored from elsewhere (an issue tracker, an automation): shown, never edited here. */
export const isReadOnlyProp = (def: PropDef): boolean =>
  (def.spec as { readOnly?: boolean }).readOnly === true;

export function propLabel(def: PropDef, t: (k: string, o?: Record<string, unknown>) => string): string {
  return def.builtin ? t(`props.${def.id}`, { defaultValue: def.name }) : def.name;
}

/**
 * The task panel's groups, one per question a person asks (UI cleanup U3), in the order they are shown; the ids are
 * also i18n keys `task.group.*`: *What is it*, *When*, *Remind me*, *How big*, *Where and with whom*, *How it
 * connects*, then *Other fields* (the person's own) and *Mirrored* (read-only values from elsewhere).
 */
export const PROP_GROUPS = ["what", "timing", "remind", "effort", "place", "links", "other"] as const;
export type PropGroup = (typeof PROP_GROUPS)[number] | "mirrored";

const BY_ID: Record<string, PropGroup> = {
  nextAction: "what",
  due: "timing",
  startAt: "timing",
  "repeat.rrule": "timing",
  priority: "effort",
  energy: "effort",
  estimateMin: "effort",
  horizon: "effort",
  context: "place",
};

export function propGroupOf(def: PropDef): PropGroup {
  // a repeat's own settings (elasticity, minimum gap, …) belong to *When*, beside the Repeats control
  if (def.id.startsWith("rec.")) return "timing";
  if (BY_ID[def.id]) return BY_ID[def.id];
  return def.kind === "tag" ? "links" : "other";
}

/** Folded unless the person opened them: groups most tasks never need. */
export const FOLDED_BY_DEFAULT: PropGroup[] = ["other", "mirrored"];
